import React, { useRef, useEffect, useState } from 'react';
import { IVideoTrack } from '../../types';
import { styled } from '@material-ui/core/styles';
import { Track } from 'twilio-video';
import useMediaStreamTrack from '../../hooks/useMediaStreamTrack/useMediaStreamTrack';
import useVideoTrackDimensions from '../../hooks/useVideoTrackDimensions/useVideoTrackDimensions';

const Video = styled('video')({
  width: '100%',
  height: '100%',
});

const VideoContainer = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: '#111',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const LoadingOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  zIndex: 2,
  fontFamily: 'sans-serif',
});

interface VideoTrackProps {
  track: IVideoTrack;
  isLocal?: boolean;
  priority?: Track.Priority | null;
}

export default function VideoTrack({ track, isLocal, priority }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Conectando câmera...');
  const mediaStreamTrack = useMediaStreamTrack(track);
  const dimensions = useVideoTrackDimensions(track);
  const isPortrait = (dimensions?.height ?? 0) > (dimensions?.width ?? 0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.muted = true;
    if (track.setPriority && priority) {
      track.setPriority(priority);
    }
    track.attach(el);

    if (track.isStarted) {
      setIsLoaded(true);
    }

    const handleStarted = () => {
      setIsLoaded(true);
    };

    track.on('started', handleStarted);

    return () => {
      track.off('started', handleStarted);
      track.detach(el);
      el.srcObject = null;

      if (track.setPriority && priority) {
        track.setPriority(null);
      }
      setIsLoaded(false);
    };
  }, [track, priority]);

  const isFrontFacing = mediaStreamTrack?.getSettings().facingMode !== 'environment';
  const style = {
    transform: isLocal && isFrontFacing ? 'scaleX(-1)' : '',
    objectFit: isPortrait || track.name.includes('screen') ? ('contain' as const) : ('cover' as const),
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoaded) {
        setLoadingMessage('Rede instável, aguardando estabilização...');
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isLoaded]);

  return (
    <VideoContainer>
      {!isLoaded && (
        <LoadingOverlay>
          <span>{loadingMessage}</span>
          <span style={{ fontSize: '11px', opacity: 0.6, marginTop: '6px' }}>Isso pode levar alguns segundos...</span>
        </LoadingOverlay>
      )}
      <Video ref={ref} style={style} autoPlay playsInline />
    </VideoContainer>
  );
}
